import { useEffect } from 'react';
import { useSquashMuteStore } from '@src/data/-squashStore';
import { Squash } from '@src/data/types';
import { SQUASH_AUDIO_BASE_URL } from '@src/data/utils';

const bg_1 = `${SQUASH_AUDIO_BASE_URL}/1_bg.mp3`;
const bg_2 = `${SQUASH_AUDIO_BASE_URL}/2_bg.mp3`;
const bg_3 = `${SQUASH_AUDIO_BASE_URL}/3_bg.mp3`;
const bg_4 = `${SQUASH_AUDIO_BASE_URL}/4_bg.mp3`;
const bg_5 = `${SQUASH_AUDIO_BASE_URL}/5_bg.mp3`;
const watering = `${SQUASH_AUDIO_BASE_URL}/watering.mp3`;
const squash_yay = `${SQUASH_AUDIO_BASE_URL}/squashYay.mp3`;

const bg_1_audio = new Audio(bg_1);
bg_1_audio.loop = true;
bg_1_audio.volume = 0.2;

const bg_2_audio = new Audio(bg_2);
bg_2_audio.loop = true;
bg_2_audio.volume = 0.2;

const bg_3_audio = new Audio(bg_3);
bg_3_audio.loop = true;
bg_3_audio.volume = 0.2;

const bg_4_audio = new Audio(bg_4);
bg_4_audio.loop = true;
bg_4_audio.volume = 0.2;

const bg_5_audio = new Audio(bg_5);
bg_5_audio.loop = true;
bg_5_audio.volume = 0.2;

const watering_audio = new Audio(watering);
watering_audio.loop = true;
watering_audio.volume = 0.1;

const squash_yay_audio = new Audio(squash_yay);
squash_yay_audio.volume = 0.2;

const bgSounds = [bg_1_audio, bg_2_audio, bg_3_audio, bg_4_audio, bg_5_audio];

export const useIntroAudio = () => {
  const [isMuted, playIntroAudio, stopIntroAudio] = useSquashMuteStore(
    (state) => [state.muted, state.playIntroAudio, state.stopIntroAudio]
  );

  useEffect(() => {
    let playingOnHide = false;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        playingOnHide = !isMuted;
        stopIntroAudio();
      } else if (playingOnHide) {
        playIntroAudio();
      }
    };

    if (!isMuted) {
      playIntroAudio();
    } else {
      stopIntroAudio();
    }

    const handleClick = () => {
      if (!isMuted) {
        playIntroAudio();
      }
      document.removeEventListener('click', handleClick);
    };

    document.addEventListener('click', handleClick);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isMuted, playIntroAudio, stopIntroAudio]);
};

export const useGameAudio = (activeSquash?: Squash, isWatering?: boolean) => {
  const { stopIntroAudio } = useSquashMuteStore((state) => ({
    stopIntroAudio: state.stopIntroAudio,
  }));

  useEffect(() => {
    // Stop intro audio when entering game
    stopIntroAudio();
  }, []);

  const [isMuted] = useSquashMuteStore((state) => [state.muted]);

  useEffect(() => {
    const playingOnHide = {
      bgSound: false,
      wateringSound: false,
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        let level = activeSquash?.onChainLevel ?? 0;
        level = Math.min(4, level);
        // Store state of background music
        playingOnHide.bgSound = !bgSounds[level].paused;
        bgSounds[level].pause();

        // Store state of watering sound and pause it
        playingOnHide.wateringSound = !watering_audio.paused;
        watering_audio.pause();
      } else {
        // Resume sounds if they were playing before
        if (playingOnHide.bgSound && !isMuted) {
          const level = activeSquash?.onChainLevel ?? 0;
          bgSounds[level].play().catch(console.error);
        }
        if (playingOnHide.wateringSound && !isMuted && isWatering) {
          watering_audio.play().catch(console.error);
        }
      }
    };

    let level = activeSquash?.onChainLevel ?? 0;
    level = Math.min(4, level);

    if (!isMuted) {
      bgSounds[level].play().catch(console.error);
    } else {
      bgSounds[level].pause();
    }

    const playAudio = () => {
      if (!isMuted) {
        bgSounds[level].play().catch(console.error);
      }
      document.removeEventListener('click', playAudio);
    };

    document.addEventListener('click', playAudio);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      bgSounds[level].pause();
      document.removeEventListener('click', playAudio);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isMuted, activeSquash?.onChainLevel]);
};
